import React from 'react';
import { Box, Grid } from '@mui/material';
import { UseFormReturn, FieldErrors } from 'react-hook-form';
import { DatePickerFormControl } from '../common/form/DatePickerFormControl';
import { TextFieldFormControl } from '../common/form/TextFieldFormControl';
import { MedicalCheckupType } from '../../models/enums/medicalCheckupType ';
import { CompanyShortDto } from '../../models/companies/companyShortDto';
import { LookupDto } from '../../models/common/lookup.dto';
import { DriverShortDto } from '../../models/drivers/driverShortDto';
import { RejectReasonDto } from '../../models/medicalCheckups/rejectReasonDto';
import TmFullButton from '../common/buttons/TmFullButton';
import CompanyAutocompleteFormControl from '../common/form/CompanyAutocompleteFormControl';
import TerminalsAutocompleteFormControl from '../common/form/TerminalsAutocompleteFormControl';
import DriversAutocompleteFormControl from '../common/form/DriversAutocompleteFormControl';
import RejectReasonsAutocompleteFormControl from '../common/form/RejectReasonsAutocompleteFormControl';
import MedicalCheckupTypesAutocompleteFormControl from '../common/form/MedicalCheckupTypesAutocompleteFormControl';

export interface RiskGroupFilterForm {
    fromDate: string;
    toDate: string;
    types: MedicalCheckupType[];
    company: CompanyShortDto | null;
    terminals: LookupDto[];
    drivers: DriverShortDto[];
    ageFrom?: string | number;
    ageTo?: string | number;
    rejectReasons: RejectReasonDto[];
}

interface RiskGroupFilterBarProps {
    methods: UseFormReturn<RiskGroupFilterForm>;
    onSubmit: (data: RiskGroupFilterForm) => void;
    onSubmitError: (errors: FieldErrors<RiskGroupFilterForm>) => void;
}

export const RiskGroupFilterBar: React.FC<RiskGroupFilterBarProps> = ({
    methods,
    onSubmit,
    onSubmitError
}) => {
    const company = methods.watch('company');

    return (
        <Box component="form" noValidate>
            <Grid container spacing={2} mb={2} mt={1}>
                <Grid container item xs={12} spacing={2}>
                    <Grid item xs={12} md={6}>
                        <CompanyAutocompleteFormControl name="company" required style={{ width: '100%' }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TerminalsAutocompleteFormControl
                            name="terminals"
                            companyId={company?.id ?? null}
                            style={{ width: '100%' }}
                        />
                    </Grid>
                </Grid>
                <Grid container item xs={12} spacing={2}>
                    <Grid item xs={12} md={6}>
                        <DriversAutocompleteFormControl name="drivers" companyId={company?.id ?? null} style={{ width: '100%', marginTop: 0 }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextFieldFormControl
                            name="ageFrom"
                            label="Возраст от"
                            type="number"
                            style={{ width: '100%' }}
                            validationRules={{
                                min: {
                                    value: 18,
                                    message: "Возраст должен быть не менее 18 лет"
                                },
                                max: {
                                    value: 120,
                                    message: "Возраст должен быть не более 120 лет"
                                },
                                validate: (value: number) => {
                                    const ageToNum = Number(methods.getValues("ageTo"));
                                    if (value !== undefined && !isNaN(ageToNum)) {
                                        if (value > ageToNum) {
                                            return "Возраст 'от' должен быть меньше возраста 'до'";
                                        }
                                    }
                                    return true;
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextFieldFormControl
                            name="ageTo"
                            label="Возраст до"
                            type="number"
                            style={{ width: '100%' }}
                            validationRules={{
                                min: {
                                    value: 18,
                                    message: "Возраст должен быть не менее 18 лет"
                                },
                                max: {
                                    value: 120,
                                    message: "Возраст должен быть не более 120 лет"
                                },
                                validate: (value: number) => {
                                    const ageFromNum = Number(methods.getValues("ageFrom"));
                                    if (value !== undefined && !isNaN(ageFromNum)) {
                                        if (value < ageFromNum) {
                                            return "Возраст 'до' должен быть больше возраста 'от'";
                                        }
                                    }
                                    return true;
                                }
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid container item xs={12} spacing={2}>
                    <Grid item xs={12} md={3}>
                        <DatePickerFormControl
                            name="fromDate"
                            label="Начало периода"
                            required
                            style={{ width: '100%' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <DatePickerFormControl
                            name="toDate"
                            label="Конец периода"
                            required
                            style={{ width: '100%' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <MedicalCheckupTypesAutocompleteFormControl name="types" style={{ width: '100%', marginTop: 0 }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <RejectReasonsAutocompleteFormControl name="rejectReasons" style={{ width: '100%', marginTop: 0 }} />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <TmFullButton
                            sx={{ minWidth: 180 }}
                            onClick={methods.handleSubmit(onSubmit, onSubmitError)}
                        >
                            Сформировать
                        </TmFullButton>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};
